/**
 * Change Request Drilldown Modal Component (CR-6381)
 *
 * Shows the list of change requests when clicking on a bar in the
 * Change Requests monthly charts.
 */
import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Loader2 } from 'lucide-react'
import { changeRequestsDrilldownAPI, type ChangeRequestDrilldownRecord } from '../../lib/api'

/** Change type labels for display */
const CHANGE_TYPE_LABELS: Record<string, string> = {
  date: 'Date',
  price: 'Price',
  qty: 'Quantity',
}

/** Initiator labels for display */
const INITIATOR_LABELS: Record<string, string> = {
  customer: 'Customer',
  supplier: 'Supplier',
}

interface ChangeRequestDrilldownModalProps {
  open: boolean
  onClose: () => void
  month: string
  changeType: 'date' | 'price' | 'qty'
  initiator?: 'customer' | 'supplier'
  supplierId?: number
  strategicOnly?: boolean
}

export function ChangeRequestDrilldownModal({
  open,
  onClose,
  month,
  changeType,
  initiator,
  supplierId,
  strategicOnly,
}: ChangeRequestDrilldownModalProps) {
  const [records, setRecords] = useState<ChangeRequestDrilldownRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !month || !changeType) return

    const fetchRecords = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await changeRequestsDrilldownAPI.get({
          month,
          change_type: changeType,
          initiator,
          supplier_id: supplierId,
          strategic_only: strategicOnly,
        })
        setRecords(response.data.records)
      } catch (err) {
        console.error('[ChangeRequestDrilldownModal] Error fetching records:', err)
        setError('Failed to load change requests. Please try again.')
        setRecords([])
      } finally {
        setLoading(false)
      }
    }

    fetchRecords()
  }, [open, month, changeType, initiator, supplierId, strategicOnly])

  const changeTypeLabel = CHANGE_TYPE_LABELS[changeType] || changeType
  const initiatorLabel = initiator ? INITIATOR_LABELS[initiator] : 'All'

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return ''
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {changeTypeLabel} Change Requests - {month}
            {initiator && ` (${initiatorLabel})`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">{error}</div>
            </div>
          )}

          {!loading && !error && records.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No change requests found for this period.
            </div>
          )}

          {!loading && !error && records.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Line</TableHead>
                  <TableHead>Part Number</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Field Changed</TableHead>
                  <TableHead>Old Value</TableHead>
                  <TableHead>New Value</TableHead>
                  <TableHead>Initiator</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell className="font-medium">{record.po_number}</TableCell>
                    <TableCell>{record.line_number}</TableCell>
                    <TableCell>{record.part_number}</TableCell>
                    <TableCell>{record.supplier_name}</TableCell>
                    <TableCell>{record.field_changed}</TableCell>
                    <TableCell className="text-muted-foreground">{record.old_value}</TableCell>
                    <TableCell>{record.new_value}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          record.initiator === 'customer'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {INITIATOR_LABELS[record.initiator] || record.initiator}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(record.change_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <span className="text-sm text-muted-foreground">
            {records.length} record{records.length !== 1 ? 's' : ''}
          </span>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
